import { generateSlug } from "@/lib/identifiers"
import { countryCodeInfo, localeInfo, supportedCountryCodes, supportedLocales } from "@/lib/locales"
import { formatAnyPhoneNumber } from "@/lib/phone"
import {
  ApiError,
  CallbackPhoneNumber,
  TaskTemplate,
  TaskTemplateCallbackRoute,
  TaskTemplateProperty,
  VibratoClient
} from "@/lib/vibrato-api"
import { commonForwardingConditions } from "@/lib/forwarding-conditions"
import { settings } from "@/settings"
import { useDropzone } from "react-dropzone"
import { Modal, ModalBody, ModalHeader, ModalFooter } from "flowbite-react"
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react"
import { AlignLeft, Attachment, MinusCircle, Plus, Check, Trash, Phone, NavArrowDown, Edit } from "iconoir-react"
import { useCallback, useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { VibratoClientContext } from "./contexts"
import { LoadingSpinner } from "../LoadingSpinner"

const defaultInstructions = `Here is an example of a good set of instructions:

- Today you're calling someone to remind them of their appointment. Greet them first: "Hello, this is (the recipient's name) calling from Vibrato".
- Then say "I'm calling to remind you of your appointment on (appointment date) at (appointment time). Please let me know if you need to reschedule or cancel."
- The call succeeds if the customer confirms the appointment.`

export default function EditTaskTemplate() {
  const navigate = useNavigate()

  const [uuid, setUuid] = useState(useParams().uuid)
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)
  const [existingTemplate, setExistingTemplate] = useState<TaskTemplate | null>(null)
  const [taskName, setTaskName] = useState("")
  const [taskDescription, setTaskDescription] = useState("")
  const [taskInstructions, setTaskInstructions] = useState(defaultInstructions)
  const [taskProperties, setTaskProperties] = useState<TaskTemplateProperty[] | null>([])
  const [recipientName, setRecipientName] = useState<string | null | undefined>(null)
  const [recipientPhoneNumber, setRecipientPhoneNumber] = useState<string | null | undefined>(null)
  const [recipientCountryCode, setRecipientCountryCode] = useState("1")
  const [callLocale, setCallLocale] = useState("en-us")
  const [message, setMessage] = useState({ text: "", type: "" })
  const [nameToSlug, setNameToSlug] = useState({} as Record<string, string>)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [useCustomProperties, setUseCustomProperties] = useState(false)
  const [fullTaskProperties, setFullTaskProperties] = useState<TaskTemplateProperty[]>([])
  const [showFullProperties, setShowFullProperties] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [reload, setReload] = useState(0)
  const [failedUploadMessage, setFailedUploadMessage] = useState("")

  // Call forwarding states
  const [callbackPhoneNumbers, setCallbackPhoneNumbers] = useState<CallbackPhoneNumber[]>([])
  const [showAddRoute, setShowAddRoute] = useState(false)
  const [editingRouteUuid, setEditingRouteUuid] = useState<string | null>(null)
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("")
  const [customCondition, setCustomCondition] = useState("")
  const [selectedPredefinedCondition, setSelectedPredefinedCondition] = useState("")
  const [routeMessage, setRouteMessage] = useState({ text: "", type: "" })

  // Inline editing states for each route
  const [editingInlineRoutes, setEditingInlineRoutes] = useState<Set<string>>(new Set())
  const [inlineEditData, setInlineEditData] = useState<{
    [key: string]: {
      phoneNumber: string
      condition: string
      predefinedCondition: string
    }
  }>({})

  useEffect(() => {
    document.title = "Vibrato - Edit Task Template"
  }, [])

  // Load callback phone numbers
  useEffect(() => {
    if (vibratoClient) {
      const fetchCallbackNumbers = async () => {
        try {
          const numbers = await vibratoClient.getCallbackPhoneNumbers()
          setCallbackPhoneNumbers(numbers)
        } catch (error) {
          console.error("Failed to fetch callback phone numbers:", error)
        }
      }
      fetchCallbackNumbers()
    }
  }, [vibratoClient])

  const addParameter = () => {
    setTaskProperties((prevProperties) => [
      ...(prevProperties || []),
      {
        name: "",
        description: "",
        slug: "",
        example: "",
        default: null,
        required: true,
        html_type: "text"
      }
    ])
  }

  useEffect(() => {
    if (uuid && vibratoClient) {
      const fetchTaskTemplate = async () => {
        try {
          console.log("fetching task template", uuid)
          const template = await vibratoClient.getTaskTemplate(uuid)
          setExistingTemplate(template)
          setTaskName(template.taskName)
          setTaskDescription(template.taskDescription)
          setTaskInstructions(template.taskInstructions)
          setTaskProperties(template.taskProperties)
          setFullTaskProperties(template.fullTaskProperties || [])
          setRecipientName(template.recipientName)
          setRecipientPhoneNumber(template.recipientPhoneNumber)
          setRecipientCountryCode(template.recipientCountryCode || "1")
          setCallLocale(template.callLocale || "en-us")
          setUseCustomProperties(template.taskProperties?.length != null)
          console.log("reloaded task template", reload)
        } catch (error) {
          console.error("Failed to fetch task template:", error)
        }
      }

      fetchTaskTemplate()
    }
  }, [reload, uuid, vibratoClient])

  const uploadUrl = () => {
    if (!existingTemplate) {
      return `${settings.VIBRATO_URL}/api/task_templates/upload_document/`
    }
    return `${settings.VIBRATO_URL}/api/task_templates/${existingTemplate.uuid}/upload_document/`
  }

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!existingTemplate) {
        setFailedUploadMessage("Please save the task template before uploading documents.")
        return
      }

      const file = acceptedFiles[0]
      if (!file) return

      setUploading(true)
      setUploadSuccess(false)
      setFailedUploadMessage("")

      const formData = new FormData()
      formData.append("file", file)

      fetch(uploadUrl(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${vibratoClient.token}`
        },
        body: formData
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Upload failed with status: ${response.status}`)
          }
          return response.json()
        })
        .then(() => {
          setUploading(false)
          setUploadSuccess(true)
          setReload((prev) => prev + 1)
          console.log(`File uploaded: ${file.name}`)
          setTimeout(() => setUploadSuccess(false), 3000)
        })
        .catch((error) => {
          setUploading(false)
          setUploadSuccess(false)
          setFailedUploadMessage(`File failed to upload: ${error.message}`)
        })
    },
    [existingTemplate, vibratoClient, uploadUrl, setReload]
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxSize: 50 * 1024 * 1024, // 50MB
    disabled: !existingTemplate
  })

  // Default to extracted props
  useEffect(() => {
    if (!useCustomProperties && fullTaskProperties.length > 0 && taskProperties?.length === 0) {
      setTaskProperties(fullTaskProperties)
    }
  }, [useCustomProperties, fullTaskProperties, taskProperties])

  const updateProperty = (index: number, updatedParam: Partial<TaskTemplateProperty>) => {
    const newParams = [...(taskProperties || [])]
    newParams[index] = { ...newParams[index], ...updatedParam }

    if (updatedParam.name != null) {
      const slug = generateSlug(updatedParam.name)
      setNameToSlug({ ...nameToSlug, [updatedParam.name]: slug })
      newParams[index].slug = slug
    }

    setTaskProperties(newParams)
  }

  const removeProperty = (index: number) => {
    const newParams = [...(taskProperties || [])]
    newParams.splice(index, 1)
    setTaskProperties(newParams)
  }

  const deleteDocument = async (documentId: number) => {
    if (existingTemplate) {
      try {
        await vibratoClient.deleteTaskTemplateDocument(existingTemplate.uuid, documentId)
        setReload((prev) => prev + 1)
      } catch (error) {
        console.error("Failed to delete document:", error)
        setMessage({ text: "Failed to delete document", type: "error" })
      }
    }
  }

  const addCallForwardingRoute = async () => {
    if (!existingTemplate || !selectedPhoneNumber) {
      setRouteMessage({ text: "Please select a phone number", type: "error" })
      return
    }

    const condition = customCondition

    if (!condition.trim()) {
      setRouteMessage({ text: "Please enter a forwarding condition", type: "error" })
      return
    }

    try {
      await vibratoClient.createTaskTemplateRoute(existingTemplate.uuid, selectedPhoneNumber, condition)
      setRouteMessage({ text: "Call forwarding route added successfully!", type: "success" })
      setShowAddRoute(false)
      setSelectedPhoneNumber("")
      setCustomCondition("")
      setSelectedPredefinedCondition("")
      setReload((prev) => prev + 1)
    } catch (error: any) {
      console.error("Failed to add route:", error)
      setRouteMessage({ text: "Error adding call forwarding route", type: "error" })
    }
  }

  const updateRoute = async () => {
    if (!existingTemplate || !editingRouteUuid || !selectedPhoneNumber) {
      setRouteMessage({ text: "Please select a phone number", type: "error" })
      return
    }

    const condition = customCondition

    if (!condition.trim()) {
      setRouteMessage({ text: "Please enter a forwarding condition", type: "error" })
      return
    }

    try {
      await vibratoClient.updateTaskTemplateRoute(
        existingTemplate.uuid,
        editingRouteUuid,
        selectedPhoneNumber,
        condition
      )
      setRouteMessage({ text: "Call forwarding route updated successfully!", type: "success" })
      setShowAddRoute(false)
      setEditingRouteUuid(null)
      setSelectedPhoneNumber("")
      setCustomCondition("")
      setSelectedPredefinedCondition("")
      setReload((prev) => prev + 1)
    } catch (error: any) {
      console.error("Failed to update route:", error)
      setRouteMessage({ text: "Error updating call forwarding route", type: "error" })
    }
  }

  const deleteRoute = async (routeUuid: string) => {
    if (!existingTemplate) return

    try {
      await vibratoClient.deleteTaskTemplateRoute(existingTemplate.uuid, routeUuid)
      setRouteMessage({ text: "Route deleted successfully!", type: "success" })
      setReload((prev) => prev + 1)
    } catch (error) {
      console.error("Failed to delete route:", error)
      setRouteMessage({ text: "Error deleting route", type: "error" })
    }
  }

  const cancelRouteEdit = () => {
    setShowAddRoute(false)
    setEditingRouteUuid(null)
    setSelectedPhoneNumber("")
    setCustomCondition("")
    setSelectedPredefinedCondition("")
    setRouteMessage({ text: "", type: "" })
  }

  const startInlineEdit = (route: TaskTemplateCallbackRoute) => {
    const newEditingRoutes = new Set(editingInlineRoutes)
    newEditingRoutes.add(route.uuid)
    setEditingInlineRoutes(newEditingRoutes)

    // Check if this matches a predefined condition
    const matchingCondition = commonForwardingConditions.find((c) => c.condition === route.forwarding_condition)

    setInlineEditData({
      ...inlineEditData,
      [route.uuid]: {
        phoneNumber: route.callback_phone_number.uuid,
        condition: route.forwarding_condition,
        predefinedCondition: matchingCondition?.id || ""
      }
    })
  }

  const cancelInlineEdit = (routeUuid: string) => {
    const newEditingRoutes = new Set(editingInlineRoutes)
    newEditingRoutes.delete(routeUuid)
    setEditingInlineRoutes(newEditingRoutes)

    const newInlineEditData = { ...inlineEditData }
    delete newInlineEditData[routeUuid]
    setInlineEditData(newInlineEditData)
  }

  const saveInlineEdit = async (routeUuid: string) => {
    if (!existingTemplate) return

    const editData = inlineEditData[routeUuid]
    if (!editData || !editData.phoneNumber || !editData.condition.trim()) {
      setRouteMessage({ text: "Please fill in all fields", type: "error" })
      return
    }

    try {
      await vibratoClient.updateTaskTemplateRoute(
        existingTemplate.uuid,
        routeUuid,
        editData.phoneNumber,
        editData.condition
      )
      setRouteMessage({ text: "Route updated successfully!", type: "success" })
      cancelInlineEdit(routeUuid)
      setReload((prev) => prev + 1)
    } catch (error: any) {
      console.error("Failed to update route:", error)
      setRouteMessage({ text: "Error updating route", type: "error" })
    }
  }

  const updateInlineEditData = (routeUuid: string, field: string, value: string) => {
    setInlineEditData({
      ...inlineEditData,
      [routeUuid]: {
        ...inlineEditData[routeUuid],
        [field]: value
      }
    })
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSaving(true)

    let cleanPhone = recipientPhoneNumber
    if (cleanPhone) {
      cleanPhone = cleanPhone.replace(/\D/g, "")
    }

    let submitProps: TaskTemplateProperty[] | null = taskProperties
    if (!useCustomProperties) {
      submitProps = null
    }

    const taskTemplateData: TaskTemplate = {
      uuid: existingTemplate?.uuid || "",
      createdAt: existingTemplate?.createdAt || "",
      updatedAt: existingTemplate?.updatedAt || "",
      taskName,
      taskDescription,
      taskInstructions: taskInstructions,
      taskProperties: submitProps,
      recipientName: recipientName || null,
      recipientPhoneNumber: cleanPhone || null,
      recipientCountryCode: recipientCountryCode,
      callLocale: callLocale
    }

    try {
      if (existingTemplate) {
        const updated = await vibratoClient.updateTaskTemplate(existingTemplate.uuid, taskTemplateData)
        setFullTaskProperties(updated.fullTaskProperties || [])
      } else {
        const newTt = await vibratoClient.createTaskTemplate(taskTemplateData)
        setUuid(newTt.uuid)
        setExistingTemplate(newTt)
        setFullTaskProperties(newTt.fullTaskProperties || [])
        window.history.replaceState(null, "", `/task-templates/${newTt.uuid}`)
      }
      setMessage({ text: "Task template saved successfully!", type: "success" })
    } catch (error: any) {
      console.error("Failed to save task template:", error)

      if (error instanceof ApiError) {
        const errorMessages: string[] = []
        if (error.data) {
          if (Array.isArray(error.data)) {
            errorMessages.push(error.data.join(", "))
          } else {
            for (const [field, messages] of Object.entries(error.data)) {
              if (Array.isArray(messages)) {
                errorMessages.push(`${field}: ${messages.join(", ")}`)
              } else {
                errorMessages.push(`${field}: ${messages}`)
              }
            }
          }
        }

        const errorMessage = `Error saving task template: ${errorMessages.join("\n")}`
        setMessage({ text: errorMessage, type: "error" })
        setIsSaving(false)
        return
      }

      setMessage({ text: "Error saving task template.", type: "error" })
      setIsSaving(false)
      return
    }
    setIsSaving(false)
  }

  const deleteTaskTemplate = async () => {
    if (!existingTemplate) {
      return
    }

    try {
      await vibratoClient.deleteTaskTemplate(existingTemplate.uuid)
    } catch (error) {
      console.error("Failed to delete task template:", error)
    }
  }

  return (
    <div className="h-full w-full p-6 md:p-8 flex flex-col gap-6">
      <DeleteConfirmationModal
        show={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={deleteTaskTemplate}
      />

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <AlignLeft width="24" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {existingTemplate ? "Edit Task Template" : "Create Task Template"}
            </h1>
            <p className="text-gray-600">Define reusable tasks for AI phone calls</p>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-gray-50 rounded-xl p-5 mb-6 border border-gray-200">
        <p className="text-sm md:text-base text-gray-600">
          Use task templates to define reusable tasks that the AI can perform during a phone call. The instructions
          define the exact steps the AI should take during the call. You can also define properties that can be
          configured when making a call.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-2xl p-6 shadow-md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="taskName" className="block text-sm font-medium text-gray-700 mb-1">
              Task Name
            </label>
            <input
              type="text"
              id="taskName"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-base px-4 py-3"
              required
            />
          </div>
          <div>
            <label htmlFor="callLanguage" className="block text-sm font-medium text-gray-700 mb-1">
              Call Language
            </label>
            <select
              id="callLanguage"
              onChange={(e) => setCallLocale(e.target.value)}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-base px-4 py-3"
              required
            >
              {supportedLocales.map((locale) => (
                <option key={locale} value={locale} selected={callLocale === locale}>
                  {localeInfo[locale].displayName}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">The language the AI uses during the call</p>
          </div>
        </div>

        <div>
          <label htmlFor="taskDescription" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="taskDescription"
            value={taskDescription}
            onChange={(e) => setTaskDescription(e.target.value)}
            className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-base px-4 py-3"
            rows={2}
            required
          />
        </div>

        <div>
          <label htmlFor="taskInstructions" className="block text-sm font-medium text-gray-700 mb-1">
            Instructions
          </label>
          <textarea
            id="taskInstructions"
            value={taskInstructions}
            onChange={(e) => setTaskInstructions(e.target.value)}
            className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-base px-4 py-3"
            rows={6}
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Enter the instructions for the AI to use during a phone call. You can reference contact custom fields or
            custom properties by name. The AI will have access to these values during the call.
          </p>
        </div>

        {!useCustomProperties && fullTaskProperties.length > 0 && (
          <button
            type="button"
            onClick={() => setShowFullProperties(!showFullProperties)}
            className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium rounded-lg flex items-center gap-2 transition-colors"
          >
            {showFullProperties ? "Hide Extracted Properties" : "View Extracted Properties"}
            <NavArrowDown className={`w-4 h-4 transition-transform ${showFullProperties ? "rotate-180" : ""}`} />
          </button>
        )}

        {!useCustomProperties && showFullProperties && (
          <div className="mt-4 bg-blue-50 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Extracted Properties</h3>
            <p className="text-sm text-gray-600 mb-4">
              The following properties were extracted from the task. You can reference these properties in the
              instructions just by mentioning them.
            </p>
            {fullTaskProperties.length === 0 && (
              <div className="text-gray-500 italic">No extracted properties available.</div>
            )}
            {fullTaskProperties.length > 0 && (
              <div className="space-y-4">
                {fullTaskProperties.map((param, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Name</label>
                        <div className="font-medium text-gray-900">{param.name}</div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Slug</label>
                        <div className="font-mono text-sm text-blue-600">{`{{${param.slug}}}`}</div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Description</label>
                        <div className="text-gray-700">{param.description || "—"}</div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Example</label>
                        <div className="text-gray-700">{param.example || "—"}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700 mb-1">
              Recipient Name (optional)
            </label>
            <input
              type="text"
              id="recipientName"
              value={recipientName || ""}
              onChange={(e) => setRecipientName(e.target.value)}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-base px-4 py-3"
              placeholder="John Doe"
            />
            <p className="mt-1 text-xs text-gray-500">
              The name of the person or business to call. Referenced in instructions.
            </p>
          </div>

          <div>
            <label htmlFor="recipientPhoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Recipient Phone Number (optional)
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Set a default phone number for this task template. When someone uses this template to make a call, this
              number will be pre-filled but can be changed if needed. Leave blank to require a phone number each time.
            </p>
            <div className="space-y-2">
              <div>
                <label htmlFor="countryCode" className="block text-xs font-medium text-gray-500 mb-1">
                  Country Code
                </label>
                <select
                  id="countryCode"
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-base px-4 py-3"
                  onChange={(e) => setRecipientCountryCode(e.target.value)}
                >
                  {supportedCountryCodes.map((countryCode) => (
                    <option
                      key={countryCodeInfo[countryCode].code}
                      value={countryCodeInfo[countryCode].code}
                      selected={recipientCountryCode === countryCodeInfo[countryCode].code}
                    >
                      {countryCodeInfo[countryCode].displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="recipientPhoneNumber" className="block text-xs font-medium text-gray-500 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  id="recipientPhoneNumber"
                  value={recipientPhoneNumber || ""}
                  placeholder="555-123-4567"
                  onChange={(e) => setRecipientPhoneNumber(e.target.value)}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-base px-4 py-3"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="bg-gray-50 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Attachment className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800">Documents</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Upload documents that the AI can use during a phone call. The AI will use the document content during the
            call.
          </p>

          {existingTemplate?.documentUploads && existingTemplate?.documentUploads?.length > 0 && (
            <div className="mb-4">
              <ul className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
                {existingTemplate?.documentUploads.map((doc) => (
                  <li key={doc.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <Attachment className="w-4 h-4 text-gray-500" />
                      <a
                        href={doc.url}
                        download={doc.name}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        {doc.name}
                      </a>
                    </div>
                    <button
                      onClick={() => deleteDocument(doc.id)}
                      className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                    >
                      <Trash className="w-4 h-4" />
                      <span className="hidden sm:inline">Remove</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-4 flex-wrap">
            {existingTemplate ? (
              <>
                <div
                  {...getRootProps({
                    className:
                      "flex max-w-[200px] h-12 px-4 text-white bg-linear-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 cursor-pointer rounded-lg shadow-md hover:shadow-lg transition-all items-center justify-center gap-2"
                  })}
                >
                  <input
                    {...getInputProps({
                      className: "hidden"
                    })}
                  />
                  {uploading ? <LoadingSpinner className="w-5 h-5 text-white" /> : <Attachment className="w-5 h-5" />}
                  <span>Upload Document</span>
                </div>

                {uploading && <span className="text-sm font-medium text-gray-600">Uploading and analyzing...</span>}
                {uploadSuccess && (
                  <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" /> Upload successful!
                  </span>
                )}
                {failedUploadMessage && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-red-600">{failedUploadMessage}</span>
                  </div>
                )}
              </>
            ) : (
              <span className="text-sm text-amber-600">Save template first to enable uploads</span>
            )}
          </div>
        </div>

        {/* Call Forwarding Conditions Section */}
        <div className="bg-gray-50 rounded-xl overflow-hidden">
          <Disclosure defaultOpen={false}>
            {({ open }) => (
              <div>
                <DisclosureButton
                  id="call-forwarding-disclosure-button"
                  className="flex w-full justify-between items-center p-5 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Phone className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <h2 className="text-lg font-semibold text-gray-800">Call Forwarding Conditions</h2>
                    </div>
                  </div>
                  <NavArrowDown
                    className={`${
                      open ? "rotate-180 transform" : ""
                    } h-5 w-5 text-blue-500 transition-transform duration-200`}
                  />
                </DisclosureButton>
                <DisclosurePanel className="border-t border-gray-200 p-5">
                  <p className="text-sm text-gray-600 mb-4">
                    Set up conditions for when incoming calls should be forwarded to your verified phone numbers. When a
                    caller mentions specific keywords or scenarios, the AI will automatically forward the call to you.
                  </p>

                  {existingTemplate ? (
                    <>
                      {/* Existing Routes */}
                      {existingTemplate.callback_routes && existingTemplate.callback_routes.length > 0 && (
                        <div className="mb-4">
                          <h3 className="text-md font-semibold text-gray-800 mb-2">Active Forwarding Routes</h3>
                          <div className="space-y-3">
                            {existingTemplate.callback_routes.map((route) => (
                              <div
                                key={route.uuid}
                                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                              >
                                {!editingInlineRoutes.has(route.uuid) ? (
                                  // View Mode
                                  <div className="p-4">
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <Phone className="w-4 h-4 text-blue-600" />
                                          <span className="font-medium text-gray-900">
                                            {formatAnyPhoneNumber(
                                              route.callback_phone_number.country_code,
                                              route.callback_phone_number.phone_number
                                            )}
                                          </span>
                                        </div>
                                        <p className="text-sm text-gray-600">{route.forwarding_condition}</p>
                                      </div>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => startInlineEdit(route)}
                                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                                          title="Edit route"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => deleteRoute(route.uuid)}
                                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                          title="Delete route"
                                        >
                                          <Trash className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  // Edit Mode
                                  <div className="p-4 bg-blue-50">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Edit Route</h4>

                                    {/* Phone Number Selection */}
                                    <div className="mb-3">
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Forward to Phone Number
                                      </label>
                                      <select
                                        value={
                                          inlineEditData[route.uuid]?.phoneNumber || route.callback_phone_number.uuid
                                        }
                                        onChange={(e) =>
                                          updateInlineEditData(route.uuid, "phoneNumber", e.target.value)
                                        }
                                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2"
                                      >
                                        {callbackPhoneNumbers.map((number) => (
                                          <option key={number.uuid} value={number.uuid}>
                                            {formatAnyPhoneNumber(number.country_code, number.phone_number)}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    {/* Condition Template Selection */}
                                    <div className="mb-3">
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Choose a template (optional)
                                      </label>
                                      <select
                                        value={inlineEditData[route.uuid]?.predefinedCondition || ""}
                                        onChange={(e) => {
                                          const selectedId = e.target.value
                                          updateInlineEditData(route.uuid, "predefinedCondition", selectedId)
                                          if (selectedId) {
                                            const condition = commonForwardingConditions.find(
                                              (c) => c.id === selectedId
                                            )
                                            if (condition) {
                                              updateInlineEditData(route.uuid, "condition", condition.condition)
                                            }
                                          }
                                        }}
                                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2"
                                      >
                                        <option value="">Select a template or write your own...</option>
                                        <optgroup label="For Individuals">
                                          {commonForwardingConditions
                                            .filter((condition) => condition.category === "individual")
                                            .map((condition) => (
                                              <option key={condition.id} value={condition.id}>
                                                {condition.title}
                                              </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="For Businesses">
                                          {commonForwardingConditions
                                            .filter((condition) => condition.category === "business")
                                            .map((condition) => (
                                              <option key={condition.id} value={condition.id}>
                                                {condition.title}
                                              </option>
                                            ))}
                                        </optgroup>
                                      </select>
                                    </div>

                                    {/* Condition Text */}
                                    <div className="mb-3">
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Condition Text
                                      </label>
                                      <textarea
                                        value={inlineEditData[route.uuid]?.condition || route.forwarding_condition}
                                        onChange={(e) => {
                                          updateInlineEditData(route.uuid, "condition", e.target.value)
                                          // Clear predefined selection when user modifies text
                                          const predefinedCondition = inlineEditData[route.uuid]?.predefinedCondition
                                          if (predefinedCondition) {
                                            const matchingCondition = commonForwardingConditions.find(
                                              (c) => c.id === predefinedCondition
                                            )
                                            if (matchingCondition && e.target.value !== matchingCondition.condition) {
                                              updateInlineEditData(route.uuid, "predefinedCondition", "")
                                            }
                                          }
                                        }}
                                        placeholder="When the caller mentions..."
                                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2"
                                        rows={3}
                                        required
                                      />
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => saveInlineEdit(route.uuid)}
                                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => cancelInlineEdit(route.uuid)}
                                        className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add Route Button/Form */}
                      {!showAddRoute ? (
                        <button
                          onClick={() => {
                            setShowAddRoute(true)
                            setCustomCondition("When the caller ")
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg flex items-center gap-2 transition-colors"
                          disabled={callbackPhoneNumbers.length === 0}
                        >
                          <Plus className="w-4 h-4" />
                          Add Forwarding Condition
                        </button>
                      ) : (
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <h3 className="text-md font-semibold text-gray-800 mb-3">
                            {editingRouteUuid ? "Edit Forwarding Route" : "Add New Forwarding Route"}
                          </h3>

                          {/* Phone Number Selection */}
                          <div className="mb-4">
                            <label htmlFor="phoneNumberSelect" className="block text-sm font-medium text-gray-700 mb-2">
                              Forward to Phone Number
                            </label>
                            <select
                              id="phoneNumberSelect"
                              value={selectedPhoneNumber}
                              onChange={(e) => setSelectedPhoneNumber(e.target.value)}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base px-4 py-3"
                              required
                            >
                              <option value="">Select a verified phone number...</option>
                              {callbackPhoneNumbers.map((number) => (
                                <option key={number.uuid} value={number.uuid}>
                                  {formatAnyPhoneNumber(number.country_code, number.phone_number)}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Condition Selection */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Forwarding Condition</label>

                            {/* Predefined Condition Templates */}
                            <div className="mb-3">
                              <label
                                htmlFor="conditionTemplate"
                                className="block text-xs font-medium text-gray-500 mb-1"
                              >
                                Choose a template (optional)
                              </label>
                              <select
                                id="conditionTemplate"
                                value={selectedPredefinedCondition}
                                onChange={(e) => {
                                  const selectedId = e.target.value
                                  setSelectedPredefinedCondition(selectedId)
                                  if (selectedId) {
                                    const condition = commonForwardingConditions.find((c) => c.id === selectedId)
                                    if (condition) {
                                      setCustomCondition(condition.condition)
                                    }
                                  }
                                }}
                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base px-4 py-3"
                              >
                                <option value="">Select a template or write your own...</option>
                                <optgroup label="For Individuals">
                                  {commonForwardingConditions
                                    .filter((condition) => condition.category === "individual")
                                    .map((condition) => (
                                      <option key={condition.id} value={condition.id}>
                                        {condition.title}
                                      </option>
                                    ))}
                                </optgroup>
                                <optgroup label="For Businesses">
                                  {commonForwardingConditions
                                    .filter((condition) => condition.category === "business")
                                    .map((condition) => (
                                      <option key={condition.id} value={condition.id}>
                                        {condition.title}
                                      </option>
                                    ))}
                                </optgroup>
                              </select>
                              {selectedPredefinedCondition && (
                                <p className="mt-1 text-xs text-gray-500">
                                  {
                                    commonForwardingConditions.find((c) => c.id === selectedPredefinedCondition)
                                      ?.description
                                  }
                                </p>
                              )}
                            </div>

                            {/* Editable Text Area */}
                            <div>
                              <label htmlFor="conditionText" className="block text-xs font-medium text-gray-500 mb-1">
                                Condition Text
                              </label>
                              <textarea
                                id="conditionText"
                                value={customCondition}
                                onChange={(e) => {
                                  setCustomCondition(e.target.value)
                                  // Clear predefined selection when user starts typing custom text
                                  if (
                                    selectedPredefinedCondition &&
                                    e.target.value !==
                                      commonForwardingConditions.find((c) => c.id === selectedPredefinedCondition)
                                        ?.condition
                                  ) {
                                    setSelectedPredefinedCondition("")
                                  }
                                }}
                                placeholder="When the caller mentions..."
                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-base px-4 py-3"
                                rows={4}
                                required
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Describe when this call should be forwarded to your phone number. You can use a template
                                above or write your own condition.
                              </p>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-3">
                            <button
                              onClick={editingRouteUuid ? updateRoute : addCallForwardingRoute}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                            >
                              {editingRouteUuid ? "Update Route" : "Add Route"}
                            </button>
                            <button
                              onClick={cancelRouteEdit}
                              className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {callbackPhoneNumbers.length === 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <p className="text-sm text-yellow-800">
                            You need to verify at least one phone number before you can set up call forwarding.
                            <a href="/phone-numbers" className="text-yellow-900 underline hover:text-yellow-700 ml-1">
                              Add a phone number
                            </a>
                          </p>
                        </div>
                      )}

                      {routeMessage.text && (
                        <div
                          className={`mt-3 px-4 py-3 rounded-lg ${
                            routeMessage.type === "success"
                              ? "bg-green-50 text-green-800 border border-green-200"
                              : "bg-red-50 text-red-800 border border-red-200"
                          }`}
                        >
                          {routeMessage.text}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-sm text-amber-600">Save template first to enable call forwarding</span>
                  )}
                </DisclosurePanel>
              </div>
            )}
          </Disclosure>
        </div>

        {/* Custom Properties Section */}
        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="useCustomProperties"
              checked={useCustomProperties}
              onChange={() => setUseCustomProperties(!useCustomProperties)}
              className="rounded text-purple-600 focus:ring-purple-500 h-4 w-4 mr-2"
            />
            <label htmlFor="useCustomProperties" className="text-sm font-medium text-gray-700">
              Use custom properties (advanced)
            </label>
          </div>

          {useCustomProperties && (
            <div className="bg-gray-50 rounded-xl p-5 mt-3">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Plus className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800">Custom Properties</h2>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Define the configurable properties that the AI can use during a phone call. You can reference these
                properties in the instructions field by their name or slug.
              </p>

              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h3 className="text-md font-semibold text-blue-800 mb-2">How Custom Properties Work</h3>
                <ul className="list-disc list-inside space-y-2 text-sm text-blue-700">
                  <li>
                    <strong>Slugs</strong>: Each property has a unique slug that's automatically generated from the
                    name. Use these slugs in your instructions by wrapping them in double curly braces, like{" "}
                    <code className="bg-blue-100 px-1 rounded">{"{{property_name}}"}</code>.
                  </li>
                  <li>
                    <strong>Names</strong>: Choose clear, descriptive names for your properties to make them easy to
                    understand.
                  </li>
                  <li>
                    <strong>Descriptions</strong>: Write detailed descriptions that explain what the property is used
                    for and any formatting requirements.
                  </li>
                  <li>
                    <strong>Examples</strong>: Provide realistic examples that show the expected format and content.
                  </li>
                </ul>
              </div>

              {taskProperties != null && taskProperties.length > 0 && (
                <div className="space-y-4 mb-4">
                  {taskProperties.map((param, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor={`propertyName-${index}`}
                            className="block text-xs font-medium text-gray-500 mb-1"
                          >
                            Name
                          </label>
                          <input
                            type="text"
                            id={`propertyName-${index}`}
                            placeholder="Name"
                            value={param.name}
                            onChange={(e) => updateProperty(index, { name: e.target.value })}
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-base px-4 py-3"
                            required
                          />
                        </div>
                        <div>
                          <label
                            htmlFor={`propertyDescription-${index}`}
                            className="block text-xs font-medium text-gray-500 mb-1"
                          >
                            Description
                          </label>
                          <input
                            type="text"
                            id={`propertyDescription-${index}`}
                            placeholder="Description"
                            value={param.description}
                            onChange={(e) => updateProperty(index, { description: e.target.value })}
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-base px-4 py-3"
                            required
                          />
                        </div>
                        <div>
                          <label
                            htmlFor={`propertyExample-${index}`}
                            className="block text-xs font-medium text-gray-500 mb-1"
                          >
                            Example
                          </label>
                          <input
                            type="text"
                            id={`propertyExample-${index}`}
                            placeholder="Example"
                            value={param.example}
                            onChange={(e) => updateProperty(index, { example: e.target.value })}
                            required
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-base px-4 py-3"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor={`propertySlug-${index}`}
                            className="block text-xs font-medium text-gray-500 mb-1"
                          >
                            Slug
                          </label>
                          <div className="flex gap-2 items-center">
                            <input
                              type="text"
                              id={`propertySlug-${index}`}
                              placeholder="Slug"
                              value={nameToSlug[param.name] || param.slug || ""}
                              disabled
                              className="block w-full rounded-lg border-gray-300 shadow-sm sm:text-base font-mono px-4 py-3"
                            />
                            <button
                              type="button"
                              onClick={() => removeProperty(index)}
                              className="p-2 text-red-600 hover:text-red-800 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              <MinusCircle className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={addParameter}
                className="px-4 py-2 bg-white border border-purple-200 hover:bg-purple-50 text-purple-600 font-medium rounded-lg flex items-center gap-2 transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Add Property
              </button>
            </div>
          )}
        </div>

        {message.text && (
          <div
            className={`px-4 py-3 rounded-lg ${
              message.type === "success"
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 justify-between pt-2">
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={isSaving}
              className={`px-5 py-2.5 bg-linear-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2 ${
                isSaving ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isSaving ? <LoadingSpinner className="w-4 h-4 text-white" /> : <Check className="w-5 h-5" />}
              {isSaving ? "Saving..." : "Save Template"}
            </button>

            {existingTemplate && (
              <button
                className="px-5 py-2.5 bg-linear-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                type="button"
                onClick={async () => {
                  const call = await vibratoClient.createCallFromTaskTemplate({
                    taskTemplateUuid: existingTemplate.uuid
                  })
                  navigate(`/calls/${call.id}/edit`)
                }}
              >
                <Phone className="w-5 h-5" />
                Create Call
              </button>
            )}
          </div>

          {existingTemplate && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirmation(true)}
              className="px-5 py-2.5 bg-white border border-red-300 hover:bg-red-50 text-red-600 font-medium rounded-lg shadow-sm hover:shadow-md transition-all flex items-center gap-2"
            >
              <Trash className="w-5 h-5" />
              Delete
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

function DeleteConfirmationModal({
  show,
  onClose,
  onConfirm
}: {
  show: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const navigate = useNavigate()
  const [message, setMessage] = useState("")
  const [isDeleting, setIsDeleting] = useState(false)

  const onDelete = async () => {
    try {
      setIsDeleting(true)
      await onConfirm()
      setMessage("Task template deleted successfully!")
      await new Promise((resolve) => setTimeout(resolve, 1000))
      navigate("/task-templates")
    } catch (error) {
      console.error("Failed to delete task template:", error)
      setMessage("Error deleting task template.")
      setIsDeleting(false)
    }
  }

  return (
    <Modal show={show} onClose={onClose} size="md" className="pt-safe">
      <ModalHeader className="border-b border-gray-200 p-6!">
        <span className="text-xl font-semibold text-gray-900">Delete Task Template</span>
      </ModalHeader>
      <ModalBody className="p-6!">
        {!message ? (
          <p className="text-gray-700">
            Are you sure you want to delete this task template? This action cannot be undone.
          </p>
        ) : (
          <div
            className={`p-3 rounded-lg ${
              message.includes("successfully") ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
            }`}
          >
            {message}
          </div>
        )}
      </ModalBody>
      <ModalFooter className="border-t border-gray-200 p-6!">
        {!message && (
          <div className="flex gap-3 w-full justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-linear-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2"
            >
              {isDeleting ? <LoadingSpinner className="w-4 h-4 text-white" /> : <Trash className="w-4 h-4" />}
              {isDeleting ? "Deleting..." : "Delete Template"}
            </button>
          </div>
        )}
      </ModalFooter>
    </Modal>
  )
}
